// @flow
// Vendor libs
import * as Cookie from 'app/packages/cookie-utils';
import context from 'app/common/lib/context';
import contextLogger from 'app/common/lib/ContextLogger';
import ExperienceManager from 'app/common/lib/ExperienceManager';
import Impressions from 'app/packages/impression-logging';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import templateConst from 'app/common/lib/templateConst';
import { unregisterServiceWorker } from 'app/common/lib/swUtil';
import {
  flush as flushRegisterActions,
  trackRegisterActionWithAuxData,
} from 'app/common/lib/util/trackRegisterAction';

const authHandshakeLoader = () =>
  import(/* webpackChunkName: "AuthHandshakeCommon" */ 'app/common/lib/AuthHandshake');

const AUTOLOGIN_LOGOUT_COOKIE_EXPIRATION_HOURS = 24 * 30; // Defaults to 1 months

export type AuthUserAttributes = {|
  can_enable_mfa: boolean,
  connected_to_facebook: boolean,
  connected_to_google: boolean,
  connected_to_youtube: boolean,
  connected_to_instagram: boolean,
  connected_to_microsoft: boolean,
  connected_to_etsy: boolean,
  connected_to_dropbox: boolean,
  country: string,
  created_at: string,
  // TODO(dalvarez): parity with backend; consolidate custom_gender and gender once BE is
  custom_gender: ?string,
  domain_url?: string,
  domain_verified?: boolean,
  email: string,
  facebook_id: '' | string,
  facebook_publish_stream_enabled: boolean,
  facebook_timeline_enabled: boolean,
  first_name: string,
  full_name: string,
  gender: ?string,
  gplus_url: ?string,
  has_password: boolean,
  has_mfa_enabled: boolean,
  id: string,
  image_large_url: string,
  image_medium_url: string,
  image_small_url: string,
  image_xlarge_url: string,
  is_employee: boolean,
  is_high_risk: boolean,
  is_partner: boolean,
  is_write_banned: boolean,
  last_name: string,
  login_state: number, // PCONST.LoginState
  nags: Object[],
  partner?: {
    account_type?: string,
    advertising_intent?: ?number,
    business_name?: string,
    created_at?: ?string,
    is_convert?: boolean,
    is_create?: boolean,
    is_linked_business?: boolean,
    contact_name?: string,
  },
  phone_country: string,
  phone_number: string,
  phone_number_end: string,
  profile_discovered_public: boolean,
  push_package_user_id: string,
  resurrection_info?: {
    resurrection_dt?: string,
  },
  show_discovered_feed: boolean,
  twitter_publish_enabled: boolean,
  twitter_url: ?string,
  type: string,
  username: string,
  verified_domains?: string[],
  verified_identity?: Object,
  website_url?: string,
  third_party_marketing_tracking_enabled?: boolean,
|};

export type UnauthUserAttributes = {|
  unauth_id: string,
|};

type UserAttributes = AuthUserAttributes | UnauthUserAttributes;

class User {
  attributes: $Shape<UserAttributes> = {};

  // $FlowFixMe: new errors found from flow 0.96 upgrade
  get(key) {
    return key in this.attributes ? this.attributes[key] : undefined;
  }

  set(key: any, val: any) {
    if (typeof key === 'object') {
      Object.assign(this.attributes, key);
    } else {
      this.attributes[key] = val;
    }
  }

  /**
   * Check if the user is authenticated.
   *
   * @return {boolean}
   */
  isAuthenticated() {
    return !!context.instance.is_authenticated;
  }

  /**
   * Check if the user is in a limited login state, which can occur if the user
   * clicks from a link in an email that logs them in, with limited capabilities.
   *
   * @return {boolean}
   */
  isLimitedLogin() {
    if (this.attributes.login_state) {
      return this.attributes.login_state === PCONST.LoginStates.LIMITED;
    }
    return false;
  }

  /**
   * Attempt to perform a cross domain autologin for the user. This automatically
   * logs the user in if they visit a supported ccTLD (pinterest.x) in unauth
   * and there is a current active session cookie on pinterest dot com.
   */
  attemptCrossDomainAutologin() {
    const handshakeDomains = templateConst.settings.CORS_HANDSHAKE_DOMAINS;
    if (handshakeDomains.indexOf(window.location.origin) !== -1 && !Cookie.get('logged_out')) {
      authHandshakeLoader().then(({ default: authHandshake }) => {
        authHandshake.verifyLoggedInStatus();
      });
    }
  }

  flushContextLogs(windowEvent: { type: string }) {
    Impressions.pause();
    contextLogger.getInstance().flushEvents(undefined, undefined, windowEvent);
  }

  /**
   * Logout the user.
   *
   * @return {!Promise.<undefined>}
   */
  logout() {
    // If the user was connected using the Facebook auto-login feature,
    // we generate a cookie that block the auto-login to happen again.
    // If we don't do this: we should use the Facebook SDK "logout"
    // method which will also disconnect the user from Facebook.
    Cookie.set(
      PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED,
      'True',
      AUTOLOGIN_LOGOUT_COOKIE_EXPIRATION_HOURS,
    );
    this.flushContextLogs({ type: 'logout' });
    return Promise.resolve(undefined);
  }

  // $FlowFixMe: new errors found from flow 0.96 upgrade
  logoutWithOptions(redirectUrl, localStorageItems: ?Object, savePassword) {
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    return new Promise((resolve, reject) => {
      flushRegisterActions(true);
      this.flushContextLogs({ type: 'logoutWithOptions' });
      ResourceFactory.create('UserSessionResource', {
        disable_auth_failure_redirect: true,
        save_password: savePassword,
      })
        .callDelete()
        .then(() => {
          trackRegisterActionWithAuxData('logout', {
            tags: { app: 'web_denzel', reason: 'user_initiated' },
          });
          storage.sessionStorage.clear();
          storage.localStorage.clear();
          ExperienceManager.instance.clearPlacedExperiences();
          Cookie.set(
            PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED,
            'True',
            AUTOLOGIN_LOGOUT_COOKIE_EXPIRATION_HOURS,
          );
        })
        .then(() => unregisterServiceWorker('logoutWithOptions'))
        .then(() => {
          if (localStorageItems) {
            for (const key in localStorageItems) {
              storage.localStorage.setItem(key, localStorageItems[key]);
            }
          }
          if (redirectUrl) {
            window.location.assign(redirectUrl);
          }

          resolve();
        })
        .catch(reject);
    });
  }

  /**
   * Returns whether the user is write banned.
   */
  isWriteBanned(): boolean {
    if (this.attributes.nags && Array.isArray(this.attributes.nags)) {
      const nags = this.attributes.nags;
      return nags.length > 0 && nags[0].type === 'write_banned';
    }
    return false;
  }
}

export default new User();
